/**
 * BPMN paint tokens — the colour half of the process render story (pixels live
 * in `metrics.ts`). Resolves the ~40 Tailwind utility classes the committed
 * `BpmnView.tsx` paints with into concrete sRGB values for ONE fixed export
 * theme, so the headless resvg pipeline (no stylesheet, no CSS vars) matches
 * what a user sees on open. Mirrors the board's `defaultTokens()` precedent.
 *
 * Fixed theme = **Islands Dark** (the app default, `theme.ts` DEFAULT_THEME).
 * Resolution sources:
 *   • `slate-*` / `sky-*` are RE-THEMED under Islands Dark — resolved from the
 *     RGB channel triplets in `app-shell/src/theme.css` (`[data-theme=
 *     "islands-dark"]`, mapped to scales by `app-shell/src/tailwind-theme.css`).
 *   • `amber-*` / `rose-*` / `purple-*` / `emerald-*` are NOT re-themed, so they
 *     fall through to Tailwind v4's stock palette. This repo is Tailwind v4
 *     (oklch), so those are the v4 oklch defaults from
 *     `node_modules/tailwindcss/theme.css`, converted to sRGB (Ottosson oklab).
 * Each token below carries its `class @ islands-dark → value` derivation.
 *
 * `/NN` opacity modifiers become `rgba()` via `alpha()` (shared with the board
 * token module) over the resolved base hex.
 */

import { alpha } from "../tokens.js";

export interface ProcessTokens {
  // ── Surface ────────────────────────────────────────────────────────────
  /** Scroll-container backdrop. */
  readonly background: string;
  /** CSS font-family list, used verbatim by the SVG backend (same stack as
   *  the board's `defaultTokens`; resvg substitutes its bundled Inter). */
  readonly fontFamily: string;

  // ── Node fills ─────────────────────────────────────────────────────────
  readonly taskFill: string;
  readonly gatewayFill: string;
  readonly eventFill: string;
  /** Compensation task's tinted body. */
  readonly compensationFill: string;

  // ── Node strokes ───────────────────────────────────────────────────────
  readonly taskStroke: string;
  readonly gatewayStroke: string;
  /** Event circle (start/intermediate/end alike — only stroke *width* varies,
   *  a metric, not a colour; there is no distinct start-vs-end colour). */
  readonly eventStroke: string;
  /** Compensation event circle. */
  readonly eventCompStroke: string;
  /** Compensation task border. */
  readonly compensationStroke: string;
  /** Text-annotation open bracket. */
  readonly annotationStroke: string;
  /** Loop/try/dist frame border (default, un-diffed). */
  readonly frameStroke: string;
  /** Selected / scrubbed node ring. */
  readonly activeStroke: string;
  /** Inbound message-catch envelope. */
  readonly inboundStroke: string;
  /** Gateway/event glyph strokes (×, +, timer, conditional, …). */
  readonly glyphStroke: string;

  // ── Glyph & marker fills ───────────────────────────────────────────────
  /** Filled event glyphs (error bolt, terminate disc). */
  readonly glyphFill: string;
  /** Activity markers (loop ↻ / MI bars / subprocess ⊞) — one shade dimmer
   *  than the gateway/event glyphs (committed ActivityMarker paints every
   *  element slate-400, BpmnView.tsx 324–357). */
  readonly markerStroke: string;
  /** The loop marker's filled arrowhead (same shade as its arc). */
  readonly markerFill: string;
  /** Compensation rewind-triangle marker on a task. */
  readonly compensationMarker: string;
  /** Arrowhead fill. Uniform (slate) in the committed view regardless of edge
   *  colour; a per-edge composer may override later. */
  readonly arrowFill: string;

  // ── Text ───────────────────────────────────────────────────────────────
  /** Task title. */
  readonly titleText: string;
  /** Secondary labels: gateway/event/edge labels + module-owner chips. */
  readonly labelText: string;
  /** Text-annotation body. (Shares `labelText`'s value today.) */
  readonly annotationText: string;
  /** Frame label + annotation owner chip. */
  readonly mutedText: string;
  /** Actor-owned chip (italic). */
  readonly actorText: string;
  /** Pickable branch (if/switch/try) label. */
  readonly branchText: string;

  // ── Edges ──────────────────────────────────────────────────────────────
  readonly edgeSeq: string;
  readonly edgeComp: string;
  readonly edgeDrop: string;
  readonly edgeReentry: string;
  readonly edgeAssoc: string;

  // ── Diff accents (union-scaffold tags) ─────────────────────────────────
  readonly diffAdded: string;
  readonly diffRemoved: string;
  /** Also the diff-detail chip text. */
  readonly diffChanged: string;

  // ── Opacity ────────────────────────────────────────────────────────────
  /** Dimmed alternative branches. */
  readonly dimOpacity: number;
  /** Diff-removed ghost. */
  readonly ghostOpacity: number;

  // ══ Swimlane surface (flow / sequence views) ═══════════════════════════
  // The committed SwimlaneView paints with inline theme hexes (not classes);
  // these carry those committed values verbatim, plus the four lane-header
  // CSS vars resolved under Islands Dark. Source lines cite the committed
  // SwimlaneView.tsx (`git show HEAD:…`).

  // ── Lane chrome ────────────────────────────────────────────────────────
  /** Lane-header chip fill (`rgb(var(--panel))`, SwimlaneView 3280). */
  readonly laneHeaderFill: string;
  /** Lane-header chip border (`rgb(var(--border))`, 3281). */
  readonly laneHeaderStroke: string;
  /** Lane-header chip text (`rgb(var(--text))`, 3283). */
  readonly laneHeaderText: string;
  /** Dashed column separators (#1e293b, 3308). */
  readonly laneSeparator: string;

  // ── Blocks ─────────────────────────────────────────────────────────────
  /** Step block body (rgba(15,23,42,.85), 1880). */
  readonly blockFill: string;
  /** Step block border + seq arrow + entry dot + comment-label text (#94a3b8). */
  readonly blockStroke: string;
  /** Step block label (#e2e8f0, 1881). */
  readonly blockText: string;
  /** Block subtitle ("from X") + prose/comment text (#94a3b8, 1882/2007). */
  readonly blockSubtitle: string;
  /** Note block border (#92400e, 1876) / body (rgba(245,158,11,.08), 1879). */
  readonly noteStroke: string;
  readonly noteFill: string;
  /** Compensation block border/chain (#f59e0b) / body (rgba(245,158,11,.12))
   *  / label (#fcd9a3) / subtitle (rgba(245,158,11,.7)) — 1877-1882. */
  readonly compStroke: string;
  readonly compFill: string;
  readonly compText: string;
  readonly compSubtitle: string;

  // ── Arrows ─────────────────────────────────────────────────────────────
  /** Flow step→step + gateway fork/cont/tail legs (#475569, 1229/2735). */
  readonly arrowFlow: string;
  /** Gateway split→head legs (#64748b, 1234). */
  readonly arrowHead: string;
  /** try-catch head leg + fail terminator (#ef4444) / its label (#f87171). */
  readonly arrowCatch: string;
  readonly arrowCatchLabel: string;
  /** `go` re-entry arcs + reentry block text (#c4b5fd, 1602/1937). */
  readonly reentryArc: string;
  /** Comment/label paint-order halo (#0f172a, 1620). */
  readonly commentHalo: string;

  // ── Gateway diamonds ───────────────────────────────────────────────────
  /** Triangle fill/stroke (#0f172a / #94a3b8, 2265-2266). */
  readonly gwTriangleFill: string;
  readonly gwTriangleStroke: string;
  /** Collapsed-pick state (fill #3a2e08, stroke + `+N` #fbbf24, 2265-2314). */
  readonly gwCollapsedFill: string;
  readonly gwCollapsedStroke: string;

  // ── Subprocess (`do`) ──────────────────────────────────────────────────
  /** Accent border/rail (#6366f1, 2021). */
  readonly subprocessAccent: string;
  /** Flow box body (rgba(99,102,241,.10), 2078) / seq band (….12, 2115) /
   *  seq section (….05, 3032). */
  readonly subprocessFillFlow: string;
  readonly subprocessFillSeq: string;
  readonly subprocessSectionFill: string;
  /** Label (#c7d2fe, 2081) / caret (#818cf8, 2090) / seq hint (#6366f1, 2132). */
  readonly subprocessText: string;
  readonly subprocessCaret: string;
  readonly subprocessHint: string;
  /** `each` loop rail + badge + `dist` tag (#a78bfa, 2977/2727). */
  readonly eachRail: string;

  // ── Terminators ────────────────────────────────────────────────────────
  /** fail pill (#ef4444) / finish pill (#10b981) / pill text (#fff), 2163-2182. */
  readonly terminatorFail: string;
  readonly terminatorFinish: string;
  readonly terminatorText: string;

  // ── Sequence frames + bands ────────────────────────────────────────────
  /** Frame rect (rgba(56,189,248,.04) / #334155, 3108-3109) + label (#94a3b8). */
  readonly seqFrameFill: string;
  readonly seqFrameStroke: string;
  readonly seqFrameLabel: string;
  /** UML-alt band divider (#334155) + labels (sel #7dd3fc / unsel #64748b). */
  readonly bandDivider: string;
  readonly bandLabelSelected: string;
  readonly bandLabelUnselected: string;
  /** Branch tab chips (3201-3204): fills, strokes, text (sel/unsel/empty). */
  readonly tabFillSelected: string;
  readonly tabFillUnselected: string;
  readonly tabStrokeSelected: string;
  readonly tabStrokeUnselected: string;
  readonly tabTextSelected: string;
  readonly tabTextUnselected: string;
  readonly tabTextEmpty: string;
}

/**
 * Islands-Dark-resolved BPMN tokens. Comment on each line: the source Tailwind
 * class, resolved under Islands Dark, and the concrete value it yields.
 */
export function defaultProcessTokens(): ProcessTokens {
  return {
    // Surface
    background: "#131314", // bg-slate-950 @ islands-dark → 19 19 20
    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',

    // Node fills
    taskFill: "#1e1f22", // fill-slate-900 @ islands-dark → 30 31 34
    gatewayFill: "#1e1f22", // fill-slate-900 @ islands-dark → 30 31 34
    eventFill: "#1e1f22", // fill-slate-900 @ islands-dark → 30 31 34
    compensationFill: alpha("#461901", 0.6), // fill-amber-950/60 (v4 stock) → rgba(70,25,1,.6)

    // Node strokes
    taskStroke: "#6f737a", // stroke-slate-500 @ islands-dark → 111 115 122
    gatewayStroke: "#6f737a", // stroke-slate-500 @ islands-dark → 111 115 122
    eventStroke: "#9da0a8", // stroke-slate-400 @ islands-dark → 157 160 168
    eventCompStroke: alpha("#fe9a00", 0.7), // stroke-amber-500/70 (v4 stock) → rgba(254,154,0,.7)
    compensationStroke: alpha("#fe9a00", 0.6), // stroke-amber-500/60 (v4 stock) → rgba(254,154,0,.6)
    annotationStroke: "#6f737a", // stroke-slate-500 @ islands-dark → 111 115 122
    frameStroke: "#5a5d63", // stroke-slate-600 @ islands-dark → 90 93 99
    activeStroke: "#548af7", // stroke-sky-400 @ islands-dark → 84 138 247
    inboundStroke: "#8fb3f8", // stroke-sky-300 @ islands-dark → 143 179 248
    glyphStroke: "#b4b8bf", // stroke-slate-300 @ islands-dark → 180 184 191

    // Glyph & marker fills
    glyphFill: "#b4b8bf", // fill-slate-300 @ islands-dark → 180 184 191
    markerStroke: "#9da0a8", // stroke-slate-400 @ islands-dark → 157 160 168
    markerFill: "#9da0a8", // fill-slate-400 @ islands-dark → 157 160 168
    compensationMarker: "#ffb900", // fill-amber-400 (v4 stock oklch → sRGB)
    arrowFill: "#9da0a8", // fill-slate-400 @ islands-dark → 157 160 168

    // Text
    titleText: "#ced0d6", // fill-slate-200 @ islands-dark → 206 208 214
    labelText: "#9da0a8", // fill-slate-400 @ islands-dark → 157 160 168
    annotationText: "#9da0a8", // fill-slate-400 @ islands-dark → 157 160 168
    mutedText: "#6f737a", // fill-slate-500 @ islands-dark → 111 115 122
    actorText: "#5ee9b5", // fill-emerald-300 (v4 stock oklch → sRGB)
    branchText: "#8fb3f8", // fill-sky-300 @ islands-dark → 143 179 248

    // Edges
    edgeSeq: "#9da0a8", // stroke-slate-400 @ islands-dark → 157 160 168
    edgeComp: alpha("#ffb900", 0.8), // stroke-amber-400/80 (v4 stock) → rgba(255,185,0,.8)
    edgeDrop: alpha("#ff637e", 0.7), // stroke-rose-400/70 (v4 stock) → rgba(255,99,126,.7)
    edgeReentry: alpha("#c27aff", 0.8), // stroke-purple-400/80 (v4 stock) → rgba(194,122,255,.8)
    edgeAssoc: "#6f737a", // stroke-slate-500 @ islands-dark → 111 115 122

    // Diff accents
    diffAdded: "#00d492", // stroke-emerald-400 (v4 stock oklch → sRGB)
    diffRemoved: "#ff637e", // stroke-rose-400 (v4 stock oklch → sRGB)
    diffChanged: "#ffb900", // stroke-amber-400 (v4 stock oklch → sRGB)

    // Opacity
    dimOpacity: 0.45, // opacity={n.dimmed ? 0.45 : …}
    ghostOpacity: 0.55, // opacity={accent.ghost ? 0.55 : …}

    // ── Swimlane: lane chrome ──
    laneHeaderFill: "#1e1f22", // rgb(var(--panel)) @ islands-dark → 30 31 34
    laneHeaderStroke: "#43454a", // rgb(var(--border)) @ islands-dark → 67 69 74
    laneHeaderText: "#d1d3d9", // rgb(var(--text)) @ islands-dark → 209 211 217
    laneSeparator: "#1e293b", // committed hex (SwimlaneView 3308)

    // ── Swimlane: blocks ──
    blockFill: "rgba(15,23,42,0.85)", // committed (1880)
    blockStroke: "#94a3b8", // committed (1877)
    blockText: "#e2e8f0", // committed (1881)
    blockSubtitle: "#94a3b8", // committed (1882)
    noteStroke: "#92400e", // committed (1876)
    noteFill: "rgba(245,158,11,0.08)", // committed (1879)
    compStroke: "#f59e0b", // committed (1877)
    compFill: "rgba(245,158,11,0.12)", // committed (1880)
    compText: "#fcd9a3", // committed (1881)
    compSubtitle: "rgba(245,158,11,0.7)", // committed (1882)

    // ── Swimlane: arrows ──
    arrowFlow: "#475569", // committed (2735)
    arrowHead: "#64748b", // committed (1234)
    arrowCatch: "#ef4444", // committed (1234)
    arrowCatchLabel: "#f87171", // committed (1266)
    reentryArc: "#c4b5fd", // committed (1602)
    commentHalo: "#0f172a", // committed (1620)

    // ── Swimlane: gateway diamonds ──
    gwTriangleFill: "#0f172a", // committed (2265)
    gwTriangleStroke: "#94a3b8", // committed (2266)
    gwCollapsedFill: "#3a2e08", // committed (2265)
    gwCollapsedStroke: "#fbbf24", // committed (2266)

    // ── Swimlane: subprocess ──
    subprocessAccent: "#6366f1", // committed (2021)
    subprocessFillFlow: "rgba(99,102,241,0.10)", // committed (2078)
    subprocessFillSeq: "rgba(99,102,241,0.12)", // committed (2115)
    subprocessSectionFill: "rgba(99,102,241,0.05)", // committed (3032)
    subprocessText: "#c7d2fe", // committed (2081)
    subprocessCaret: "#818cf8", // committed (2090)
    subprocessHint: "#6366f1", // committed (2132)
    eachRail: "#a78bfa", // committed (2977)

    // ── Swimlane: terminators ──
    terminatorFail: "#ef4444", // committed (2163)
    terminatorFinish: "#10b981", // committed (2163)
    terminatorText: "#ffffff", // committed (2182)

    // ── Swimlane: sequence frames + bands ──
    seqFrameFill: "rgba(56,189,248,0.04)", // committed (3108)
    seqFrameStroke: "#334155", // committed (3109)
    seqFrameLabel: "#94a3b8", // committed (3114)
    bandDivider: "#334155", // committed (3139)
    bandLabelSelected: "#7dd3fc", // committed (3150)
    bandLabelUnselected: "#64748b", // committed (3150)
    tabFillSelected: "rgba(56,189,248,0.18)", // committed (3201)
    tabFillUnselected: "rgba(148,163,184,0.06)", // committed (3202)
    tabStrokeSelected: "#0ea5e9", // committed (3203)
    tabStrokeUnselected: "#475569", // committed (3203)
    tabTextSelected: "#7dd3fc", // committed (3204)
    tabTextUnselected: "#cbd5e1", // committed (3204)
    tabTextEmpty: "#64748b", // committed (3204)
  };
}

// There is deliberately NO owner→colour hash here. Scene does not derive colour
// from a model value: it cannot reach the palette's `swatchFor` (dependency-free
// by design), and a local approximation would be a second hash disagreeing with
// the product's one. `composeBpmn` paints an uncoloured owner as chrome and lets
// whoever owns the value space supply the tint via `ComposeBpmnOptions.ownerColor`.
